// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import './styles.css';
import { loadBundle } from './data';
import { initTooltip, hideTooltip } from './components/tooltip';
import { closeDrawer, initDrawer } from './drawer';
import { GLOSSARY, lookup } from './glossary';
import { esc, period } from './format';
import type { Bundle } from './types';

import * as overview from './views/overview';
import * as deaths from './views/deaths';
import * as cost from './views/cost';
import * as psych from './views/psych';
import * as injuries from './views/injuries';
import * as industries from './views/industries';
import * as enforcement from './views/enforcement';
import * as hazards from './views/hazards';
import * as explorer from './views/explorer';

interface View {
  title: string;
  render(b: Bundle): string;
  mount?(root: HTMLElement, b: Bundle, rerender: () => void): void | Promise<void>;
}

const VIEWS: Record<string, View> = {
  overview, deaths, cost, psych, injuries, industries, enforcement, hazards, explorer,
};

const ORDER = ['overview', 'deaths', 'cost', 'psych', 'injuries', 'industries', 'enforcement', 'hazards', 'explorer'];

let bundle: Bundle | null = null;
let current = 'overview';

// ── Shell ──────────────────────────────────────────────────────────────────

function shell(): string {
  return `
    <a class="skip" href="#main">Skip to content</a>
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#/overview">
          <svg class="brand-mark" viewBox="0 0 32 32" aria-hidden="true">
            <path d="M16 4.5c-4.4 0-8 2.8-8 6.3v1.5h16v-1.5c0-3.5-3.6-6.3-8-6.3z" fill="currentColor"/>
            <rect x="6" y="13.2" width="20" height="2.6" rx="1.3" fill="currentColor"/>
          </svg>
          <span class="brand-txt"><strong>Work Injuries</strong><small>Safe Work Australia data</small></span>
        </a>
        <nav class="tabs" aria-label="Views">
          ${ORDER.map((k) => `<a href="#/${k}" data-view="${k}">${esc(VIEWS[k].title)}</a>`).join('')}
        </nav>
        <button type="button" class="about-btn" id="about-open" aria-label="About this site and its data">?</button>
      </div>
    </header>
    <main class="main-content" id="main"><div class="loading">Loading Safe Work Australia data…</div></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <span>Data: <a href="https://data.safeworkaustralia.gov.au/" target="_blank" rel="noopener">Safe Work Australia</a> © Commonwealth of Australia, <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a>. Boundaries: ABS ASGS (CC BY 4.0).</span>
        <span>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></span>
      </div>
    </footer>`;
}

// ── About modal ────────────────────────────────────────────────────────────
//
// The dismissal contract, in full: a ✕ with a real hit area, a scrim that
// closes on pointerdown, Escape from anywhere, and — critically — the modal is
// REMOVED from the DOM when closed rather than hidden with an attribute that a
// class-supplied `display` would silently override.

let modalHost: HTMLDivElement | null = null;
let modalLastFocus: HTMLElement | null = null;

/**
 * A dialog that declares aria-modal="true" promises a screen reader that
 * nothing outside it is reachable. Without a trap, Tab walks straight out into
 * the page behind — which the reader still believes is hidden — so the promise
 * is worse than not making it. Same contract as the drawer.
 */
function trapFocus(container: HTMLElement, e: KeyboardEvent): void {
  if (e.key !== 'Tab') return;
  const focusable = [...container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
  )].filter((el) => el.offsetParent !== null || el === document.activeElement);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  else if (!container.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
}

function aboutHtml(b: Bundle): string {
  const m = b.meta;
  const P = b.claims.periods;
  return `
    <h2>About this site</h2>
    <p>Every figure here comes from Safe Work Australia, the national policy body for work health and safety. It publishes the data as five separate Excel workbooks with more than a hundred sheets between them, and nothing joins them together. This site is that join.</p>

    <h3>What is counted</h3>
    <ul>
      <li><strong>Work deaths</strong> are traumatic injury fatalities — people killed by an injury sustained doing their job, ${m.generated.fatalityYears[0]}–${m.generated.fatalityYears[1]}. Deaths from occupational disease are <em>not</em> included: they take decades to appear and cannot be attributed to a year of exposure. They are counted separately, and undercounted everywhere.</li>
      <li><strong>Serious claims</strong> are accepted workers’ compensation claims involving a week or more off work, ${period(P[0])} to ${period(P[P.length - 1])}. Claims that were rejected, never lodged, or resolved in under a week are not here — so this is a floor on the real harm, not a measure of it.</li>
      <li><strong>Enforcement</strong> figures are what each work health and safety regulator reported doing: inspectors fielded, workplaces visited, notices issued, proceedings brought, fines ordered.</li>
    </ul>

    <h3>Three things worth knowing before you quote a number</h3>
    <ul>
      <li><strong>The newest year is preliminary and will rise.</strong> Claims keep being accepted after a year closes. For the same reason, both cost measures are withheld entirely for ${period(P[P.length - 1])} — compensation is still accruing — so no figure on this site describes a record set in the most recent year.</li>
      <li><strong>A dash is not a zero.</strong> Safe Work Australia withholds a cell when the annual claim count is under five, or the statistical error is too large. Those cells are shown as dashes, never plotted as zero, never interpolated, and always sorted to the bottom of a ranking rather than the top.</li>
      <li><strong>Death counts are revised for years afterwards.</strong> As coronial investigations conclude, past years move${m.revisions.length ? ` — ${m.revisions.map((r) => `${r.year} was first published as ${r.archive} and now stands at ${r.current}`).join(', ')}` : ''}. The long series here takes every shared year from the current release and marks where the older one stops.</li>
    </ul>

    <h3>What this site deliberately does not show</h3>
    <ul>
      <li><strong>No chain from cause to injury.</strong> The four TOOCS codings — what went wrong, how the body was hurt, which part was damaged, what object was involved — are published as four separate tables of totals with no record linking them. A flow diagram built from separate totals would show connections nobody measured.</li>
      <li><strong>No local geography.</strong> The finest spatial unit anywhere in this data is the state or territory, and even that is muddied because a “jurisdiction” is a compensation scheme rather than a place. There is no suburb, council or postcode view because there is no suburb, council or postcode data.</li>
      <li><strong>No case-level prosecutions.</strong> Safe Work Australia keeps a prosecutions register but publishes it only through a dashboard, with no downloadable file. Only aggregate enforcement counts appear here.</li>
    </ul>

    <h3>How the data is checked</h3>
    <p>The build runs ${m.gates.length} reconciliation gates against the source files and refuses to publish if any of them fails. They check that two independently-published tables of the same deaths agree exactly; that every category’s children account for their parent, allowing for the publisher’s own “fewer than five” suppression rule; that the stitched death series takes every shared year from the current release; and that a known quirk in the premium tables still behaves as it did when this was built.</p>
    <ul>${m.gates.map((g) => `<li>${esc(g.name)} — ${g.checks} checks, ${g.fails} failures</li>`).join('')}</ul>
    <p>Two axes carry a genuine unattributed remainder, which is disclosed rather than absorbed into a category: the industry divisions fall ${Math.abs(m.residuals.industry[m.residuals.industry.length - 1] ?? 0)} claims short of the published total in ${period(P[P.length - 1])}, and the two nature-of-injury groups fall ${Math.abs(m.residuals.nature[m.residuals.nature.length - 1] ?? 0)} short.</p>

    <h3>Sources</h3>
    <ul>${m.sources.map((s) => `<li>${esc(s.name)} — ${esc(s.publisher)}, ${esc(s.licence)}</li>`).join('')}</ul>
    <p>Updated annually, in step with the source releases. This is a statistical archive, not a live feed, and it is not advice — if you have been injured at work, talk to your regulator or your union.</p>`;
}

function openAbout(b: Bundle): void {
  closeAbout();
  modalHost = document.createElement('div');
  modalHost.className = 'modal-host';

  const scrim = document.createElement('div');
  scrim.className = 'modal-scrim';
  scrim.setAttribute('aria-hidden', 'true');

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'About this site');
  modal.innerHTML = `<button type="button" class="modal-close" aria-label="Close">✕</button>${aboutHtml(b)}`;

  modalHost.append(scrim, modal);
  modalLastFocus = document.activeElement as HTMLElement | null;
  document.body.appendChild(modalHost);
  document.body.classList.add('drawer-open');

  // Scrim on pointerdown (never focusable); button on click, so Enter and
  // Space reach it. Never both on one control — that double-fires on a phone.
  scrim.addEventListener('pointerdown', (e) => { e.preventDefault(); closeAbout(); });
  modal.querySelector<HTMLButtonElement>('.modal-close')?.addEventListener('click', (e) => { e.preventDefault(); closeAbout(); });
  modal.addEventListener('keydown', (e) => trapFocus(modal, e));
  modal.querySelector<HTMLButtonElement>('.modal-close')?.focus();
}

function closeAbout(): void {
  if (!modalHost) return;
  modalHost.remove();
  modalHost = null;
  if (!document.querySelector('.drawer-host')) document.body.classList.remove('drawer-open');
  const back = modalLastFocus && document.contains(modalLastFocus) ? modalLastFocus : document.getElementById('about-open');
  modalLastFocus = null;
  back?.focus();
}

// ── Glossary popover ───────────────────────────────────────────────────────

let glossPop: HTMLDivElement | null = null;
let glossLastFocus: HTMLElement | null = null;

function closeGloss(): void {
  glossPop?.remove();
  glossPop = null;
  const back = glossLastFocus;
  glossLastFocus = null;
  if (back && document.contains(back)) back.focus();
}

function openGloss(anchor: HTMLElement, key: string): void {
  closeGloss();
  const entry = lookup(key);
  if (!entry) return;
  glossLastFocus = anchor;
  glossPop = document.createElement('div');
  glossPop.className = 'gloss-pop visible';
  // A non-modal disclosure, NOT a dialog: role="dialog" tells a screen reader
  // it has entered something modal, and nothing here is. `group` plus a live
  // region announcement is honest about what it actually is.
  glossPop.setAttribute('role', 'group');
  glossPop.setAttribute('aria-label', entry.term);
  glossPop.setAttribute('tabindex', '-1');
  glossPop.innerHTML = `<button type="button" class="gloss-close" aria-label="Close definition">✕</button><strong>${esc(entry.term)}</strong><p>${esc(entry.body)}</p>`;
  document.body.appendChild(glossPop);
  anchor.setAttribute('aria-expanded', 'true');

  const r = anchor.getBoundingClientRect();
  const w = Math.min(340, window.innerWidth - 24);
  glossPop.style.width = `${w}px`;
  const left = Math.min(Math.max(12, r.left), window.innerWidth - w - 12);
  const popH = glossPop.getBoundingClientRect().height || 160;
  const below = r.bottom + 8;
  glossPop.style.left = `${left}px`;
  glossPop.style.top = `${below + popH > window.innerHeight - 12 ? Math.max(12, r.top - popH - 8) : below}px`;

  glossPop.querySelector<HTMLButtonElement>('.gloss-close')?.addEventListener('click', (e) => { e.preventDefault(); closeGloss(); });
  // Focus the popover itself so its content is read, and so Escape/Tab behave.
  glossPop.focus();
}

// ── Router ─────────────────────────────────────────────────────────────────

function parseHash(): string {
  const raw = location.hash.replace(/^#\/?/, '').split('&')[0];
  return VIEWS[raw] ? raw : 'overview';
}

function setActive(view: string): void {
  document.querySelectorAll<HTMLAnchorElement>('.tabs a').forEach((a) => {
    const on = a.dataset.view === view;
    a.classList.toggle('active', on);
    if (on) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
  });
}

function renderView(): void {
  if (!bundle) return;
  const main = document.getElementById('main');
  if (!main) return;
  const view = VIEWS[current];

  // Nothing survives a navigation: a stale tooltip, an open overlay or a
  // glossary popover left over from the previous view is a defect.
  hideTooltip();
  closeGloss();

  try {
    main.innerHTML = view.render(bundle);
  } catch (err) {
    main.innerHTML = `<div class="error"><p>Something went wrong rendering this view.</p><p class="ctl-note">${esc(String(err))}</p></div>`;
    return;
  }
  setActive(current);
  main.scrollTop = 0;
  void view.mount?.(main, bundle, renderView);
}

function navigate(): void {
  const next = parseHash();
  if (next !== current) { closeDrawer(); current = next; }
  renderView();
}

// ── Boot ───────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = shell();

  initTooltip();
  initDrawer();

  document.getElementById('about-open')?.addEventListener('click', () => { if (bundle) openAbout(bundle); });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (glossPop) { closeGloss(); return; }
    if (modalHost) closeAbout();
  });

  // Delegated: glossary affordances are rendered inside view HTML strings.
  document.addEventListener('click', (e) => {
    const link = (e.target as Element)?.closest?.<HTMLElement>('.gloss-link');
    if (link) {
      e.preventDefault();
      const already = glossPop !== null && glossLastFocus === link;
      document.querySelectorAll('.gloss-link[aria-expanded]').forEach((el) => el.removeAttribute('aria-expanded'));
      if (already) { closeGloss(); return; }
      openGloss(link, link.dataset.term ?? '');
      return;
    }
    if (glossPop && !glossPop.contains(e.target as Node)) closeGloss();
  });

  window.addEventListener('hashchange', navigate);

  const main = document.getElementById('main');
  try {
    bundle = await loadBundle();
  } catch (err) {
    if (main) {
      main.innerHTML = `<div class="error"><p>Could not load the data.</p><p class="ctl-note">${esc(String(err))}</p><p><button type="button" class="btn" id="retry">Try again</button></p></div>`;
      document.getElementById('retry')?.addEventListener('click', () => { void boot(); });
    }
    return;
  }

  current = parseHash();
  renderView();
}

void GLOSSARY;
void boot();
